import type { QueryClient } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, resolveCharIdFromJwt } from '@/lib/axios';

export interface DailyTaskRow {
  id: number;
  task_id: string;
  name: string;
  description: string | null;
  system_id: number;
  required_quantity: number;
  icon_url: string | null;
  reward_codename128: string;
  SN_Name?: string;
  reward_quantity: number;
  reward_image_url: string | null;
  order: number;
  isactive: number;
}

export interface DailyTaskProgress {
  id?: number;
  current_progress: number;
  completed: number;
  claimed: number;
}

export interface DailyTaskWithProgress {
  task: DailyTaskRow;
  progress: DailyTaskProgress;
}

export interface DailyTaskFinalReward {
  id: number;
  reward_codename128: string;
  SN_Name?: string;
  reward_quantity: number;
  reward_image_url: string | null;
  order: number;
  isactive: number;
}

export interface DailyTasksStats {
  total: number;
  completed: number;
  claimed: number;
}

export interface DailyTasksState {
  success: boolean;
  tasks: DailyTaskWithProgress[];
  finalRewards: DailyTaskFinalReward[];
  claimedFinalRewards: number[];
  stats: DailyTasksStats;
  today: string;
  error?: string;
}

export interface DailyTasksProgressResponse {
  success: boolean;
  tasks: DailyTaskWithProgress[];
  stats: DailyTasksStats;
}

export interface DailyTasksMutationResult {
  success: boolean;
  message?: string;
}

const PROGRESS_POLL_MS = 30_000;

function dailyTasksQueryKey(charId: number): (string | number)[] {
  return ['daily-tasks', charId];
}

async function fetchDailyTasks(): Promise<DailyTasksState> {
  const { data } = await api.get<DailyTasksState>('/api/v1/daily-tasks');
  return data;
}

async function fetchDailyTasksProgress(): Promise<DailyTasksProgressResponse> {
  const { data } = await api.get<DailyTasksProgressResponse>('/api/v1/daily-tasks/progress');
  return data;
}

export function useDailyTasks() {
  const charId = resolveCharIdFromJwt();
  return useQuery({
    queryKey: dailyTasksQueryKey(charId),
    queryFn: fetchDailyTasks,
    enabled: charId > 0,
    staleTime: 15_000,
  });
}

export function useDailyTasksProgressPoll(enabled: boolean) {
  const charId = resolveCharIdFromJwt();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['daily-tasks-progress', charId],
    queryFn: async () => {
      const data = await fetchDailyTasksProgress();
      if (data.success) {
        queryClient.setQueryData<DailyTasksState>(dailyTasksQueryKey(charId), (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            tasks: data.tasks,
            stats: data.stats,
          };
        });
      }
      return data;
    },
    enabled: enabled && charId > 0,
    refetchInterval: PROGRESS_POLL_MS,
    staleTime: PROGRESS_POLL_MS - 1000,
  });
}

export function useClaimDailyTask() {
  const queryClient = useQueryClient();
  const charId = resolveCharIdFromJwt();

  return useMutation({
    mutationFn: async (taskId: string) => {
      const { data } = await api.post<DailyTasksMutationResult>('/api/v1/daily-tasks/claim', {
        task_id: taskId,
      });
      return data;
    },
    onSuccess: (data) => {
      if (data.success) {
        window.setTimeout(() => {
          void queryClient.invalidateQueries({ queryKey: dailyTasksQueryKey(charId) });
        }, 1200);
      }
    },
  });
}

export function useClaimDailyTaskFinal() {
  const queryClient = useQueryClient();
  const charId = resolveCharIdFromJwt();

  return useMutation({
    mutationFn: async (finalRewardId: number) => {
      const { data } = await api.post<DailyTasksMutationResult>(
        '/api/v1/daily-tasks/final/claim',
        { final_reward_id: finalRewardId },
      );
      return data;
    },
    onSuccess: (data) => {
      if (data.success) {
        window.setTimeout(() => {
          void queryClient.invalidateQueries({ queryKey: dailyTasksQueryKey(charId) });
        }, 1200);
      }
    },
  });
}

export function prefetchDailyTasks(queryClient: QueryClient, charId: number): void {
  if (!charId) return;
  void queryClient.prefetchQuery({
    queryKey: dailyTasksQueryKey(charId),
    queryFn: fetchDailyTasks,
    staleTime: 15_000,
  });
}
